import { createFileRoute } from "@tanstack/react-router";
import { sortedPosts } from "@/data/posts";
import { PostCard } from "@/components/PostCard";
import { MgidAd } from "@/components/MgidAd";
import { siteConfig } from "@/config";

export const Route = createFileRoute("/")({
  head: () => {
    const orgLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteConfig.siteName,
      url: siteConfig.domain,
      logo: `${siteConfig.domain}/favicon.ico`,
      contactPoint: [
        {
          "@type": "ContactPoint",
          email: siteConfig.contactEmail,
          contactType: "customer support",
          availableLanguage: "English",
        },
      ],
    };
    const websiteLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteConfig.siteName,
      url: siteConfig.domain,
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${siteConfig.domain}/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    };
    return {
      meta: [
        { title: `${siteConfig.siteName} — CRM, ERP & SaaS Reviews for Modern Businesses` },
        {
          name: "description",
          content:
            "In-depth reviews, comparisons, and buyer's guides for CRM, ERP, and SaaS software — helping US businesses choose the right tools.",
        },
        {
          name: "keywords",
          content: "CRM reviews, ERP comparisons, SaaS buyer's guide, B2B software reviews",
        },
        { property: "og:title", content: siteConfig.siteName },
        { property: "og:description", content: siteConfig.description },
        { property: "og:url", content: `${siteConfig.domain}/` },
        { property: "og:image", content: siteConfig.defaultOgImage },
      ],
      links: [{ rel: "canonical", href: `${siteConfig.domain}/` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(orgLd) },
        { type: "application/ld+json", children: JSON.stringify(websiteLd) },
      ],
    };
  },
  component: Index,
});

function Index() {
  const posts = sortedPosts();
  return (
    <>
      <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
        <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
            The {siteConfig.siteName} Journal
          </p>
          <h1 className="max-w-3xl text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
            B2B software reviews and buyer's guides for people who actually use the tools.
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Hands-on comparisons across CRM, ERP, and SaaS — written for US business decision
            makers who need signal, not spin.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <h2 className="mb-6 text-2xl font-bold tracking-tight text-foreground">Latest articles</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p, i) => (
            <PostCard key={p.id} post={p} priority={i === 0} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-12">
        <MgidAd widgetId="2074285" minHeight={600} label="You may also like" />
      </section>
    </>
  );
}
